import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  putFileToPresignedUrl,
  resolveFileContentType,
} from "../utils/s3Upload";
import { API_URL } from "../config/api";
const STOCK_CATEGORY_OPTIONS = ["Stock", "Non-Stock"];
const MAX_MATERIAL_IMAGES = 5;
const MAX_MATERIAL_ATTACHMENTS = 10;

function parseApiResponse(raw) {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }
  if (data?.body != null) {
    if (typeof data.body === "string") {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }
  return data;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const success = responseData.success;
  return (
    success === true || success === "true" || success === 1 || success === "1"
  );
}

function extractCategoriesList(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return [];
  }

  let list =
    dataBlock.categories ||
    dataBlock.categoryList ||
    dataBlock.items ||
    root.categories;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.categories || inner.categoryList || inner.items;
    }
  }

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  return Array.isArray(list) ? list : [];
}

function extractSubcategoriesList(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return [];
  }

  let list =
    dataBlock.subcategories ||
    dataBlock.subcategoryList ||
    dataBlock.items ||
    root.subcategories;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.subcategories || inner.subcategoryList || inner.items;
    }
  }

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  return Array.isArray(list) ? list : [];
}

function extractAttributesList(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return [];
  }

  let list =
    dataBlock.attributes ||
    dataBlock.attributeList ||
    dataBlock.items ||
    root.attributes;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.attributes || inner.attributeList || inner.items;
    }
  }

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  return Array.isArray(list) ? list : [];
}

function getPresignedUploadUrl(meta) {
  return String(
    meta?.uploadUrl ||
      meta?.url ||
      meta?.presignedUrl ||
      meta?.signedUrl ||
      "",
  ).trim();
}

function mapPresignMeta(meta, file, fallbackType) {
  return {
    uploadUrl: getPresignedUploadUrl(meta),
    s3Key: meta?.s3Key || meta?.key,
    originalFileName:
      meta?.originalFileName || meta?.fileName || file?.name,
    contentType: meta?.contentType || fallbackType,
  };
}

function normalizePresignFileMetas(responseData, files, contentTypesForPresign) {
  if (!isApiSuccess(responseData)) return null;

  const dataBlock =
    responseData?.data && typeof responseData.data === "object"
      ? responseData.data
      : responseData;
  const nestedBlock =
    dataBlock?.data && typeof dataBlock.data === "object"
      ? dataBlock.data
      : null;

  const fallbackType = (idx) =>
    contentTypesForPresign?.[idx] ||
    resolveFileContentType(files[idx]) ||
    "application/octet-stream";

  const fileLists = [
    dataBlock?.files,
    responseData?.files,
    nestedBlock?.files,
  ];
  for (const list of fileLists) {
    if (Array.isArray(list) && list.length > 0) {
      return list.map((meta, idx) =>
        mapPresignMeta(meta, files[idx], fallbackType(idx)),
      );
    }
  }

  const urlLists = [
    dataBlock?.uploadUrls,
    nestedBlock?.uploadUrls,
    responseData?.uploadUrls,
  ];
  for (const list of urlLists) {
    if (Array.isArray(list) && list.length > 0) {
      const source = dataBlock?.uploadUrls ? dataBlock : nestedBlock || responseData;
      return list.map((uploadUrl, idx) => ({
        uploadUrl,
        s3Key: source?.s3Keys?.[idx],
        originalFileName: source?.originalFileNames?.[idx] || files[idx]?.name,
        contentType: source?.contentTypes?.[idx] || fallbackType(idx),
      }));
    }
  }

  const single =
    (getPresignedUploadUrl(dataBlock) && dataBlock?.s3Key && dataBlock) ||
    (getPresignedUploadUrl(nestedBlock) && nestedBlock?.s3Key && nestedBlock) ||
    (getPresignedUploadUrl(responseData) && responseData?.s3Key && responseData);

  if (single && files.length === 1) {
    return [mapPresignMeta(single, files[0], fallbackType(0))];
  }

  return null;
}

const MaterialCreate = () => {
  const navigate = useNavigate();

  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [materialCode, setMaterialCode] = useState("");
  const [materialSeq, setMaterialSeq] = useState("");
  const [productName, setProductName] = useState("");
  const [hsnCode, setHsnCode] = useState("");
  const [uom, setUom] = useState("");
  const [stockCategory, setStockCategory] = useState("Stock");
  const [partNumber, setPartNumber] = useState("");
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [attachmentFiles, setAttachmentFiles] = useState([]);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingSubcategories, setIsLoadingSubcategories] = useState(false);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [isLoadingNextMaterialCode, setIsLoadingNextMaterialCode] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [makeBrandOem, setMakeBrandOem] = useState("");
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [filteredAttributeOptions, setFilteredAttributeOptions] = useState([]);
  const [showAttributeDropdown, setShowAttributeDropdown] = useState(false);
  const [selectedAttributeIndex, setSelectedAttributeIndex] = useState(-1);
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(true);
  const [makeBrandOemId, setMakeBrandOemId] = useState("");
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateMaterials, setDuplicateMaterials] = useState([]);
  const [duplicateOverlayLeft, setDuplicateOverlayLeft] = useState(0);

  const [materialDescription, setMaterialDescription] = useState("");
  const [isDescriptionManuallyEdited, setIsDescriptionManuallyEdited] =
    useState(false);

  const selectedCategory = useMemo(
    () =>
      categoryOptions.find(
        (c) => String(c.categoryId || "") === String(categoryId),
      ) || null,
    [categoryOptions, categoryId],
  );

  const selectedSubcategory = useMemo(
    () =>
      subcategoryOptions.find(
        (s) => String(s.subcategoryId || "") === String(subcategoryId),
      ) || null,
    [subcategoryOptions, subcategoryId],
  );

  const categoryRef = selectedCategory?._id || selectedCategory?.id || "";
  const subcategoryRef =
    selectedSubcategory?._id || selectedSubcategory?.id || "";

  const clearNextMaterialCode = () => {
    setMaterialCode("");
    setMaterialSeq("");
  };

  useEffect(() => {
    if (isDescriptionManuallyEdited) return;

    const parts = [
      selectedCategory?.categoryName,
      selectedSubcategory?.subcategoryName,
      productName.trim(),
    ].filter(Boolean);

    let desc = parts.join(" - ");
    if (partNumber.trim()) desc += ` | Part No: ${partNumber.trim()}`;
    if (makeBrandOem.trim())
      desc += ` | Make/Brand/OEM: ${makeBrandOem.trim()}`;

    setMaterialDescription(desc.toUpperCase());
  }, [
    selectedCategory,
    selectedSubcategory,
    productName,
    partNumber,
    makeBrandOem,
    isDescriptionManuallyEdited,
  ]);

  const fetchNextMaterialCode = useCallback(
    async (nextCategoryId, nextSubcategoryId, refs = {}) => {
      const trimmedCategoryId = String(nextCategoryId || "").trim();
      const trimmedSubcategoryId = String(nextSubcategoryId || "").trim();

      if (!trimmedCategoryId || !trimmedSubcategoryId) {
        clearNextMaterialCode();
        setIsLoadingNextMaterialCode(false);
        return;
      }

      setIsLoadingNextMaterialCode(true);
      clearNextMaterialCode();

      try {
        const payload = {
          action: "next-material",
          categoryId: trimmedCategoryId,
          subcategoryId: trimmedSubcategoryId,
        };

        if (refs.categoryRef) payload.categoryRef = refs.categoryRef;
        if (refs.subcategoryRef) payload.subcategoryRef = refs.subcategoryRef;

        const response = await axios.post(API_URL, payload);
        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        const data = responseData?.data || {};
        const nextCode =
          data.nextMaterialCode || data.materialCode || data.nextCode || "";

        if (isApiSuccess(responseData) && nextCode) {
          setMaterialCode(String(nextCode));
          setMaterialSeq(String(data.materialSeq || ""));
        } else {
          toast.error(
            responseData?.message || "Failed to fetch next material code",
            { autoClose: 2000 },
          );
        }
      } catch (error) {
        console.error("Fetch next material code error:", error);
        const errData = error.response?.data
          ? parseApiResponse(error.response.data)
          : null;
        toast.error(
          (typeof errData === "object" && errData?.message) ||
            "An error occurred while fetching next material code",
          { autoClose: 2000 },
        );
      } finally {
        setIsLoadingNextMaterialCode(false);
      }
    },
    [],
  );

  const fetchCategories = useCallback(async () => {
    setIsLoadingCategories(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-categories",
        page: 1,
        limit: 500,
        sortBy: "categoryId",
        sortOrder: "asc",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      let list = responseData?.data?.categories;
      if (!Array.isArray(list)) {
        list = extractCategoriesList(responseData);
      }
      setCategoryOptions(
        isApiSuccess(responseData) && Array.isArray(list) ? list : [],
      );
    } catch (error) {
      console.error("Fetch categories error:", error);
      setCategoryOptions([]);
      toast.error("Failed to load categories", { autoClose: 2000 });
    } finally {
      setIsLoadingCategories(false);
    }
  }, []);

  const fetchSubcategories = useCallback(async (selectedCategoryId) => {
    const trimmed = String(selectedCategoryId || "").trim();
    if (!trimmed) {
      setSubcategoryOptions([]);
      return;
    }

    setIsLoadingSubcategories(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-subcategories",
        page: 1,
        limit: 500,
        categoryId: trimmed,
        sortBy: "subcategoryId",
        sortOrder: "asc",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      let list = responseData?.data?.subcategories;
      if (!Array.isArray(list)) {
        list = extractSubcategoriesList(responseData);
      }
      setSubcategoryOptions(
        isApiSuccess(responseData) && Array.isArray(list) ? list : [],
      );
    } catch (error) {
      console.error("Fetch subcategories error:", error);
      setSubcategoryOptions([]);
      toast.error("Failed to load subcategories", { autoClose: 2000 });
    } finally {
      setIsLoadingSubcategories(false);
    }
  }, []);

  const fetchUnits = useCallback(async () => {
    setIsLoadingUnits(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-units",
        page: 1,
        limit: 500,
        sortBy: "unit",
        sortOrder: "asc",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const list = Array.isArray(responseData?.data?.units)
        ? responseData.data.units
        : [];
      const activeUnits = list
        .map((item) => ({
          id: item?._id || item?.id || "",
          unit: String(item?.unit || "").trim(),
          status: String(item?.status || "").trim(),
        }))
        .filter((item) => item.unit)
        .filter(
          (item) => !item.status || item.status.toLowerCase() === "active",
        );
      setUnitOptions(isApiSuccess(responseData) ? activeUnits : []);
    } catch (error) {
      console.error("Fetch units error:", error);
      setUnitOptions([]);
      toast.error("Failed to load units", { autoClose: 2000 });
    } finally {
      setIsLoadingUnits(false);
    }
  }, []);

  const fetchAttributes = useCallback(async () => {
    setIsLoadingAttributes(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-attribute",
        page: 1,
        limit: 500,
        sortBy: "attribute",
        sortOrder: "asc",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      let list = responseData?.data?.attributes;
      if (!Array.isArray(list)) {
        list = extractAttributesList(responseData);
      }
      const activeAttributes = (Array.isArray(list) ? list : [])
        .map((item) => ({
          id: item?._id || item?.id || "",
          attribute: String(
            item?.attribute || item?.attributeName || "",
          ).trim(),
          status: String(item?.status || "").trim(),
        }))
        .filter((item) => item.attribute)
        .filter(
          (item) => !item.status || item.status.toLowerCase() === "active",
        );
      const finalList = isApiSuccess(responseData) ? activeAttributes : [];
      setAttributeOptions(finalList);
      setFilteredAttributeOptions(finalList);
    } catch (error) {
      console.error("Fetch attributes error:", error);
      setAttributeOptions([]);
      setFilteredAttributeOptions([]);
      toast.error("Failed to load attributes", { autoClose: 2000 });
    } finally {
      setIsLoadingAttributes(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchUnits();
    fetchAttributes();
  }, [fetchCategories, fetchUnits, fetchAttributes]);

  useEffect(() => {
    fetchSubcategories(categoryId);
  }, [categoryId, fetchSubcategories]);

  useEffect(() => {
    if (!categoryId || !subcategoryId) {
      clearNextMaterialCode();
      return;
    }
    fetchNextMaterialCode(categoryId, subcategoryId, {
      categoryRef,
      subcategoryRef,
    });
  }, [
    categoryId,
    subcategoryId,
    categoryRef,
    subcategoryRef,
    fetchNextMaterialCode,
  ]);

  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [imagePreviews]);

  const handleCategoryChange = (e) => {
    setCategoryId(e.target.value);
    setSubcategoryId("");
    clearNextMaterialCode();
  };

  const handleSubcategoryChange = (e) => {
    setSubcategoryId(e.target.value);
    clearNextMaterialCode();
  };

  const handleMakeBrandOemChange = (e) => {
    const value = e.target.value.toUpperCase();
    setMakeBrandOem(value);
    setMakeBrandOemId("");
    setShowAttributeDropdown(true);
    setSelectedAttributeIndex(-1);

    if (value.trim() === "") {
      setFilteredAttributeOptions(attributeOptions);
    } else {
      const filtered = attributeOptions.filter((opt) =>
        opt.attribute.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredAttributeOptions(filtered);
    }
  };

  const handleMakeBrandOemKeyDown = (e) => {
    if (!showAttributeDropdown || filteredAttributeOptions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedAttributeIndex((prev) =>
          prev < filteredAttributeOptions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedAttributeIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (
          selectedAttributeIndex >= 0 &&
          selectedAttributeIndex < filteredAttributeOptions.length
        ) {
          handleAttributeSelect(
            filteredAttributeOptions[selectedAttributeIndex],
          );
        } else if (filteredAttributeOptions.length === 1) {
          handleAttributeSelect(filteredAttributeOptions[0]);
        }
        break;
      case "Escape":
        setShowAttributeDropdown(false);
        setSelectedAttributeIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleAttributeSelect = (attributeOption) => {
    setMakeBrandOem(String(attributeOption.attribute || "").toUpperCase());
    setMakeBrandOemId(attributeOption.id);
    setShowAttributeDropdown(false);
    setSelectedAttributeIndex(-1);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";

    if (files.length === 0) return;

    const imageOnly = files.filter((file) =>
      String(file.type || "").startsWith("image/"),
    );
    if (imageOnly.length !== files.length) {
      toast.error("Only image files are allowed", { autoClose: 2000 });
    }
    if (imageOnly.length === 0) return;

    setImageFiles((prev) => {
      const next = [...prev, ...imageOnly].slice(0, MAX_MATERIAL_IMAGES);
      if (prev.length + imageOnly.length > MAX_MATERIAL_IMAGES) {
        toast.error(`Maximum ${MAX_MATERIAL_IMAGES} images allowed`, {
          autoClose: 2000,
        });
      }
      return next;
    });

    setImagePreviews((prev) => {
      const newUrls = imageOnly.map((file) => URL.createObjectURL(file));
      const next = [...prev, ...newUrls].slice(0, MAX_MATERIAL_IMAGES);
      if (next.length < prev.length + newUrls.length) {
        newUrls.slice(next.length - prev.length).forEach((url) => {
          URL.revokeObjectURL(url);
        });
      }
      return next;
    });
  };

  const removeImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const clearImages = () => {
    imagePreviews.forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
    setImageFiles([]);
    setImagePreviews([]);
  };

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";

    if (files.length === 0) return;

    setAttachmentFiles((prev) => {
      const next = [...prev, ...files].slice(0, MAX_MATERIAL_ATTACHMENTS);
      if (prev.length + files.length > MAX_MATERIAL_ATTACHMENTS) {
        toast.error(`Maximum ${MAX_MATERIAL_ATTACHMENTS} attachments allowed`, {
          autoClose: 2000,
        });
      }
      return next;
    });
  };

  const removeAttachment = (index) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAttachments = () => {
    setAttachmentFiles([]);
  };

  const validateForm = () => {
    if (!categoryId) {
      toast.error("Category is required", { autoClose: 2000 });
      return false;
    }
    if (!subcategoryId) {
      toast.error("Subcategory is required", { autoClose: 2000 });
      return false;
    }
    if (!materialCode.trim()) {
      toast.error("Material code is not ready yet. Please wait.", {
        autoClose: 2000,
      });
      return false;
    }
    if (!productName.trim()) {
      toast.error("Product name is required", { autoClose: 2000 });
      return false;
    }
    if (!hsnCode.trim()) {
      toast.error("HSN code is required", { autoClose: 2000 });
      return false;
    }
    if (!uom.trim()) {
      toast.error("UOM is required", { autoClose: 2000 });
      return false;
    }
    if (!stockCategory.trim()) {
      toast.error("Stock category is required", { autoClose: 2000 });
      return false;
    }
    return true;
  };

  const uploadMaterialFiles = async (files, code, label = "files") => {
    if (!files || files.length === 0) return [];

    const trimmedCode = String(code || "").trim();
    if (!trimmedCode) {
      toast.error(`Material code is required before uploading ${label}`, {
        autoClose: 2000,
      });
      return null;
    }

    const contentTypesForPresign = files.map((f) => resolveFileContentType(f));

    const payload = {
      action: "get-material-image-upload-presigned-url",
      materialCode: trimmedCode,
      fileNames: files.map((f) => f.name),
      contentTypes: contentTypesForPresign,
      expiresIn: 7200,
    };

    if (files.length === 1) {
      payload.fileName = files[0].name;
      payload.contentType = contentTypesForPresign[0];
    }

    const response = await axios.post(API_URL, payload);
    const responseData = normalizeApiPayload(parseApiResponse(response.data));
    const fileMetas = normalizePresignFileMetas(
      responseData,
      files,
      contentTypesForPresign,
    );

    if (!fileMetas || fileMetas.length !== files.length) {
      toast.error(
        responseData?.message || `Failed to get material ${label} upload URLs`,
        { autoClose: 2000 },
      );
      return null;
    }

    // S3 PUT via raw fetch — never shared API axios (currentUserId ~44B bug).
    try {
      for (let idx = 0; idx < files.length; idx += 1) {
        const fileMeta = fileMetas[idx];
        const file = files[idx];
        const contentType =
          fileMeta.contentType ||
          contentTypesForPresign[idx] ||
          "application/octet-stream";
        await putFileToPresignedUrl(fileMeta.uploadUrl, file, contentType);
      }
    } catch (error) {
      console.error(`Upload material ${label} error:`, error);
      toast.error(
        error?.message || `Failed to upload material ${label}`,
        { autoClose: 2500 },
      );
      return null;
    }

    return fileMetas.map((meta, idx) => ({
      s3Key: meta.s3Key,
      originalFileName: meta.originalFileName || files[idx].name,
      contentType:
        meta.contentType ||
        contentTypesForPresign[idx] ||
        "application/octet-stream",
    }));
  };

  const uploadMaterialImages = (files, code) =>
    uploadMaterialFiles(files, code, "images");

  const uploadMaterialAttachments = (files, code) =>
    uploadMaterialFiles(files, code, "attachments");

  const checkDuplicateMaterial = async () => {
    try {
      // Match on product name only. Auto description words (category, brand, etc.)
      // dilute % and hide real name matches below the 70% threshold.
      const response = await axios.post(API_URL, {
        action: "check-material-duplicate",
        categoryId,
        subcategoryId,
        productName: productName.trim().toUpperCase(),
        materialDescription: "",
      });

      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const payload =
        responseData?.data &&
        typeof responseData.data === "object" &&
        !Array.isArray(responseData.data)
          ? { ...responseData, ...responseData.data }
          : responseData;

      const materials = Array.isArray(payload?.materials)
        ? payload.materials
        : [];

      const isDuplicate =
        payload?.duplicate === true ||
        payload?.duplicate === "true" ||
        payload?.duplicate === 1 ||
        payload?.duplicate === "1";

      if (
        (payload?.success === true ||
          payload?.success === "true" ||
          payload?.success === 1 ||
          payload?.success === "1") &&
        isDuplicate &&
        materials.length > 0
      ) {
        return materials;
      }

      return [];
    } catch (error) {
      console.log("Duplicate Check Error:", error);
      return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const userId = localStorage.getItem("id");
    if (!userId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => navigate("/"), 2000);
      return;
    }

    setIsSubmitting(true);
    try {
      const duplicates = await checkDuplicateMaterial();

      if (duplicates.length > 0) {
        setDuplicateMaterials(duplicates);
        setShowDuplicateModal(true);
        setIsSubmitting(false);
        return;
      }
      await performCreate();
    } catch (error) {
      console.error("Duplicate check error:", error);
      toast.error("An error occurred while validating the material", {
        autoClose: 2000,
      });
      setIsSubmitting(false);
    }
  };

  const performCreate = async () => {
    try {
      let uploadedImages = [];
      if (imageFiles.length > 0) {
        uploadedImages = await uploadMaterialImages(imageFiles, materialCode);
        if (uploadedImages === null) return;
      }

      let uploadedAttachments = [];
      if (attachmentFiles.length > 0) {
        uploadedAttachments = await uploadMaterialAttachments(
          attachmentFiles,
          materialCode,
        );
        if (uploadedAttachments === null) return;
      }

      const userId = localStorage.getItem("id");
      const payload = {
        action: "create-material",
        userId,
        createdBy: userId,
        categoryId,
        subcategoryId,
        productName: productName.trim().toUpperCase(),
        hsnCode: hsnCode.trim().toUpperCase(),
        uom: uom.trim(),
        stockCategory: stockCategory.trim(),
        materialDescription: materialDescription.trim().toUpperCase(),
      };

      if (partNumber.trim()) payload.partNumber = partNumber.trim().toUpperCase();
      if (makeBrandOem.trim())
        payload.makeBrandOem = makeBrandOem.trim().toUpperCase();
      if (makeBrandOemId) payload.makeBrandOemId = makeBrandOemId;
      if (materialCode) payload.materialCode = materialCode;
      if (materialSeq) payload.materialSeq = materialSeq;
      if (categoryRef) payload.categoryRef = categoryRef;
      if (subcategoryRef) payload.subcategoryRef = subcategoryRef;

      if (uploadedImages.length > 0) {
        payload.images = uploadedImages;
        payload.s3Keys = uploadedImages.map((img) => img.s3Key);
        payload.originalFileNames = uploadedImages.map(
          (img) => img.originalFileName,
        );
        payload.contentTypes = uploadedImages.map((img) => img.contentType);
      }
      if (uploadedAttachments.length > 0) {
        payload.attachments = uploadedAttachments;
      }

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        const createdCode =
          responseData?.data?.materialCode ||
          responseData?.data?.materialId ||
          "";
        toast.success(
          responseData?.message ||
            (createdCode
              ? `Material ${createdCode} created successfully`
              : "Material created successfully"),
          { autoClose: 2000 },
        );
        setTimeout(() => navigate("/material", { replace: true }), 1200);
      } else {
        toast.error(responseData?.message || "Failed to create material", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Create material error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        (typeof errData === "object" && errData?.message) ||
          "An error occurred while creating material",
        { autoClose: 2000 },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceedDuplicate = async () => {
    setShowDuplicateModal(false);
    setDuplicateMaterials([]);
    setIsSubmitting(true);
    await performCreate();
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateModal(false);
    setDuplicateMaterials([]);
  };

  useEffect(() => {
    if (!showDuplicateModal) return undefined;

    const updateOverlayPosition = () => {
      const contentWrapper = document.querySelector(".content-wrapper");
      const left = contentWrapper
        ? Math.max(0, contentWrapper.getBoundingClientRect().left)
        : 0;
      setDuplicateOverlayLeft(left);
    };

    updateOverlayPosition();
    window.addEventListener("resize", updateOverlayPosition);
    return () => window.removeEventListener("resize", updateOverlayPosition);
  }, [showDuplicateModal]);

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Create Material</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/material">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Material Code
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            value={
                              isLoadingNextMaterialCode
                                ? "Fetching next code..."
                                : materialCode ||
                                  (categoryId && subcategoryId
                                    ? ""
                                    : "Select category & subcategory")
                            }
                            disabled
                            readOnly
                          />
                          <small className="text-muted">
                            {materialSeq
                              ? `Sequence: ${materialSeq}`
                              : "Fetched via next-material after category & subcategory are selected."}
                          </small>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Category <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            className="form-control"
                            value={categoryId}
                            onChange={handleCategoryChange}
                            disabled={isLoadingCategories || isSubmitting}
                            required
                          >
                            <option value="">Select category</option>
                            {categoryOptions.map((category) => {
                              const id = category.categoryId || "";
                              return (
                                <option
                                  key={id || category._id || category.id}
                                  value={id}
                                >
                                  {id} - {category.categoryName || "-"}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Subcategory <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            className="form-control"
                            value={subcategoryId}
                            onChange={handleSubcategoryChange}
                            disabled={
                              !categoryId ||
                              isLoadingSubcategories ||
                              isSubmitting
                            }
                            required
                          >
                            <option value="">
                              {categoryId
                                ? "Select subcategory"
                                : "Select category first"}
                            </option>
                            {subcategoryOptions.map((subcategory) => {
                              const id = subcategory.subcategoryId || "";
                              return (
                                <option
                                  key={id || subcategory._id || subcategory.id}
                                  value={id}
                                >
                                  {id} - {subcategory.subcategoryName || "-"}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Product Name <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Product Name"
                            value={productName}
                            onChange={(e) =>
                              setProductName(e.target.value.toUpperCase())
                            }
                            disabled={isSubmitting}
                            required
                            style={{ textTransform: "uppercase" }}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Part Number
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Part Number"
                            value={partNumber}
                            onChange={(e) =>
                              setPartNumber(e.target.value.toUpperCase())
                            }
                            disabled={isSubmitting}
                            style={{ textTransform: "uppercase" }}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Make / Brand / OEM
                        </label>
                        <div className="col-12 col-md-8">
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={
                                isLoadingAttributes
                                  ? "Loading attributes..."
                                  : "Type to search or enter Make / Brand / OEM..."
                              }
                              value={makeBrandOem}
                              onChange={handleMakeBrandOemChange}
                              onKeyDown={handleMakeBrandOemKeyDown}
                              style={{ textTransform: "uppercase" }}
                              onFocus={() => setShowAttributeDropdown(true)}
                              onBlur={() => {
                                setTimeout(() => {
                                  setShowAttributeDropdown(false);
                                  setSelectedAttributeIndex(-1);
                                }, 200);
                              }}
                              autoComplete="off"
                              disabled={isSubmitting}
                            />
                            {isLoadingAttributes && (
                              <div
                                style={{
                                  position: "absolute",
                                  right: "10px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  pointerEvents: "none",
                                }}
                              >
                                <i className="fa fa-spinner fa-spin text-muted"></i>
                              </div>
                            )}
                            {showAttributeDropdown &&
                              !isLoadingAttributes &&
                              filteredAttributeOptions.length > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    zIndex: 1000,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  {filteredAttributeOptions.map(
                                    (option, index) => (
                                      <div
                                        key={option.id || option.attribute}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          handleAttributeSelect(option);
                                        }}
                                        style={{
                                          padding: "8px 12px",
                                          cursor: "pointer",
                                          borderBottom: "1px solid #f0f0f0",
                                          backgroundColor:
                                            index === selectedAttributeIndex
                                              ? "#e9ecef"
                                              : "#fff",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor =
                                            "#f8f9fa";
                                          setSelectedAttributeIndex(index);
                                        }}
                                        onMouseLeave={(e) => {
                                          if (
                                            index !== selectedAttributeIndex
                                          ) {
                                            e.currentTarget.style.backgroundColor =
                                              "#fff";
                                          }
                                        }}
                                      >
                                        {option.attribute}
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                            {showAttributeDropdown &&
                              !isLoadingAttributes &&
                              makeBrandOem &&
                              filteredAttributeOptions.length === 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    backgroundColor: "#fff",
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    padding: "8px 12px",
                                    zIndex: 1000,
                                    color: "#6c757d",
                                    fontSize: "13px",
                                  }}
                                >
                                  No matching attributes — will be used as
                                  entered
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Material Description
                        </label>
                        <div className="col-12 col-md-8">
                          <textarea
                            className="form-control"
                            rows={2}
                            placeholder="Auto-generated from Category, Subcategory, Product Name, Part Number and Make/Brand/OEM — edit if needed"
                            value={materialDescription}
                            onChange={(e) => {
                              setMaterialDescription(
                                e.target.value.toUpperCase(),
                              );
                              setIsDescriptionManuallyEdited(true);
                            }}
                            disabled={isSubmitting}
                            style={{ textTransform: "uppercase" }}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          HSN Code <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="HSN Code"
                            value={hsnCode}
                            onChange={(e) =>
                              setHsnCode(e.target.value.toUpperCase())
                            }
                            disabled={isSubmitting}
                            required
                            style={{ textTransform: "uppercase" }}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          UOM <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            className="form-control"
                            value={uom}
                            onChange={(e) => setUom(e.target.value)}
                            disabled={isSubmitting || isLoadingUnits}
                            required
                          >
                            <option value="">
                              {isLoadingUnits
                                ? "Loading units..."
                                : "Select UOM"}
                            </option>
                            {unitOptions.map((opt) => (
                              <option key={opt.id || opt.unit} value={opt.unit}>
                                {opt.unit}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Stock Category <span className="text-danger">*</span>
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            className="form-control"
                            value={stockCategory}
                            onChange={(e) => setStockCategory(e.target.value)}
                            disabled={isSubmitting}
                            required
                          >
                            {STOCK_CATEGORY_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Images
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={
                                imageFiles.length === 0
                                  ? "No images selected"
                                  : `${imageFiles.length} image(s) selected`
                              }
                            />
                            <div className="input-group-append">
                              <label
                                className="btn btn-outline-secondary mb-0"
                                style={{ cursor: "pointer" }}
                              >
                                Browse
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  hidden
                                  onChange={handleImageChange}
                                  disabled={
                                    isSubmitting ||
                                    !materialCode ||
                                    isLoadingNextMaterialCode ||
                                    imageFiles.length >= MAX_MATERIAL_IMAGES
                                  }
                                />
                              </label>
                              {imageFiles.length > 0 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={clearImages}
                                  disabled={isSubmitting}
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                          </div>
                          <small className="text-muted">
                            Optional. Up to {MAX_MATERIAL_IMAGES} images
                            (JPG/PNG).
                            {!materialCode
                              ? " Select category & subcategory first so images can upload under the material code."
                              : ` Uploaded under Material-Images/${materialCode}/.`}
                          </small>

                          {imagePreviews.length > 0 && (
                            <div
                              className="d-flex flex-wrap mt-3"
                              style={{ gap: "10px" }}
                            >
                              {imagePreviews.map((preview, index) => (
                                <div
                                  key={`${preview}-${index}`}
                                  style={{
                                    position: "relative",
                                    width: "88px",
                                    height: "88px",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                    border: "1px solid #dee2e6",
                                  }}
                                >
                                  <img
                                    src={preview}
                                    alt={imageFiles[index]?.name || "preview"}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => removeImage(index)}
                                    disabled={isSubmitting}
                                    style={{
                                      position: "absolute",
                                      top: "4px",
                                      right: "4px",
                                      padding: "0 6px",
                                      lineHeight: "18px",
                                      fontSize: "11px",
                                    }}
                                    title="Remove"
                                  >
                                    <i className="fa-solid fa-xmark" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Attachments
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={
                                attachmentFiles.length === 0
                                  ? "No attachments selected"
                                  : `${attachmentFiles.length} attachment(s) selected`
                              }
                            />
                            <div className="input-group-append">
                              <label
                                className="btn btn-outline-secondary mb-0"
                                style={{ cursor: "pointer" }}
                              >
                                Browse
                                <input
                                  type="file"
                                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                                  multiple
                                  hidden
                                  onChange={handleAttachmentChange}
                                  disabled={
                                    isSubmitting ||
                                    !materialCode ||
                                    isLoadingNextMaterialCode ||
                                    attachmentFiles.length >=
                                      MAX_MATERIAL_ATTACHMENTS
                                  }
                                />
                              </label>
                              {attachmentFiles.length > 0 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={clearAttachments}
                                  disabled={isSubmitting}
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                          </div>
                          <small className="text-muted">
                            Optional. Up to {MAX_MATERIAL_ATTACHMENTS} files
                            (images, PDF, documents).
                            {!materialCode
                              ? " Select category & subcategory first so attachments can upload under the material code."
                              : ` Uses the same presigned upload as images under Material-Images/${materialCode}/.`}
                          </small>

                          {attachmentFiles.length > 0 && (
                            <ul className="list-group mt-3">
                              {attachmentFiles.map((file, index) => (
                                <li
                                  key={`${file.name}-${file.size}-${index}`}
                                  className="list-group-item d-flex justify-content-between align-items-center py-2"
                                >
                                  <span
                                    className="text-truncate mr-2"
                                    title={file.name}
                                  >
                                    <i className="fa-solid fa-paperclip mr-2" />
                                    {file.name}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removeAttachment(index)}
                                    disabled={isSubmitting}
                                    title="Remove"
                                  >
                                    <i className="fa-solid fa-xmark" />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={
                          isSubmitting ||
                          isLoadingNextMaterialCode ||
                          !materialCode
                        }
                      >
                        {isSubmitting ? "Creating..." : "Create Material"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showDuplicateModal && (
        <div
          tabIndex="-1"
          style={{
            position: "fixed",
            top: 0,
            left: duplicateOverlayLeft,
            right: 0,
            bottom: 0,
            width: `calc(100vw - ${duplicateOverlayLeft}px)`,
            height: "100vh",
            background: "rgba(0,0,0,.45)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            boxSizing: "border-box",
          }}
        >
          <div
            className="modal-content"
            style={{
              maxWidth: "1100px",
              width: "100%",
              margin: 0,
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">
                  <i className="fa fa-exclamation-triangle mr-2"></i>
                  Material Match Found
                </h5>

                <button
                  type="button"
                  className="close text-white"
                  onClick={handleCancelDuplicate}
                >
                  <span className="text-white">&times;</span>
                </button>
              </div>

              <div className="modal-body" style={{ overflowY: "auto" }}>
                <p className="text-danger mb-3">
                  Similar material(s) found with at least 70% word match. Please
                  review the existing records before creating a new one.
                </p>

                <div
                  className="table-responsive"
                  style={{ maxHeight: "350px", overflowY: "auto" }}
                >
                  <table
                    className="table table-bordered table-hover mb-0"
                    style={{ tableLayout: "fixed", width: "100%" }}
                  >
                    <thead className="thead-light sticky-top">
                      <tr>
                        <th style={{ width: "5%" }}>S.No</th>
                        <th style={{ width: "14%" }}>Material Code</th>
                        <th style={{ width: "22%" }}>Product Name</th>
                        <th style={{ width: "20%" }}>Description</th>
                        <th style={{ width: "23%" }}>Matched Words</th>
                        <th style={{ width: "16%" }}>Match %</th>
                      </tr>
                    </thead>

                    <tbody>
                      {duplicateMaterials.map((item, index) => {
                        const matchPercent = Number(item.matchPercent) || 0;
                        const badgeClass =
                          matchPercent >= 90
                            ? "badge-danger"
                            : matchPercent >= 80
                              ? "badge-warning"
                              : "badge-info";
                        const matchedWords = Array.isArray(item.matchedWords)
                          ? item.matchedWords.join(", ")
                          : "-";

                        return (
                          <tr
                            key={
                              item.materialCode ||
                              item._id ||
                              `dup-${index}`
                            }
                          >
                            <td>{index + 1}</td>
                            <td style={{ wordBreak: "break-word" }}>
                              <strong>{item.materialCode || "-"}</strong>
                            </td>
                            <td style={{ wordBreak: "break-word" }}>
                              {item.productName || "-"}
                            </td>
                            <td style={{ wordBreak: "break-word" }}>
                              {item.materialDescription || "-"}
                            </td>
                            <td
                              style={{
                                wordBreak: "break-word",
                                fontSize: "14px",
                              }}
                            >
                              {matchedWords}
                            </td>
                            <td style={{ wordBreak: "break-word" }}>
                              <span className={`badge ${badgeClass}`}>
                                {matchPercent}%
                              </span>
                              {item.matchSummary ? (
                                <div className="small text-muted mt-1">
                                  {item.matchSummary}
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={handleCancelDuplicate}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-info"
                  onClick={handleProceedDuplicate}
                >
                  Proceed
                </button>
              </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialCreate;
